import { IconLock, IconCheck, IconKey, IconMapPin, IconMessage } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

interface LockedSectionProps {
  sectionId: 'thanks' | 'doorCode' | 'directions' | 'custom'
  title: string
  description: string
  icon?: React.ReactNode
}

/**
 * ============================================================
 * SECCIONS CONDICIONALS - POST-BETA 1.0
 * ============================================================
 * 
 * Aquestes seccions estan preparades per ser activades després
 * de la Beta 1.0. El motor de visibilitat i herència està
 * documentat a: tasks/BACKLOG.md
 * 
 * Seccions planificades:
 * - thanks: Agraïments (quan tots els viatgers registrats)
 * - doorCode: Codi d'accés (X hores abans check-in)
 * - directions: Com arribar (sempre visible per defecte)
 * - custom: Personalitzada (per reserva específica)
 * 
 * Per activar: Implementar motor d'herència i configuració
 * a PortalConfigTemplate.sectionsConfig
 * 
 * Data creació: 27-NOV-2025
 * ============================================================
 */
export function LockedSection({ sectionId, title, description, icon }: LockedSectionProps) {
  const { t } = useTranslation('guestPortal')
  
  return (
    <div 
      className="relative overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50/50 p-6"
      data-section-id={sectionId}
      data-locked="true"
    >
      {/* Overlay amb cadenat */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100/80 to-gray-200/80 backdrop-blur-[1px]" />
      
      {/* Contingut */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center py-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 mb-3">
          {icon || <IconLock className="w-6 h-6 text-gray-400" />}
        </div>
        
        <h3 className="text-lg font-medium text-gray-500 mb-1">
          {title}
        </h3>
        
        <p className="text-sm text-gray-400 max-w-xs">
          {description}
        </p>
        
        <span className="mt-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          {t('sections.comingSoon', 'Properament')}
        </span>
      </div>
    </div>
  )
}

/**
 * Secció Agraïments - Visible quan tots els viatgers estan registrats
 */
export function SectionThanks({ locked = true }: { locked?: boolean }) {
  const { t } = useTranslation('guestPortal')
  
  if (locked) {
    return (
      <LockedSection 
        sectionId="thanks"
        title={t('sections.thanks.title', 'Gràcies per registrar-vos!')}
        description={t('sections.thanks.lockedDesc', 'Missatge de benvinguda i instruccions de check-in')}
        icon={<IconCheck className="w-6 h-6 text-gray-400" />}
      />
    )
  }
  
  // TODO: Implementar versió activa post-Beta
  return null
}

/**
 * Secció Codi d'Accés - Visible X hores abans del check-in
 */
export function SectionDoorCode({ locked = true }: { locked?: boolean }) {
  const { t } = useTranslation('guestPortal')
  
  if (locked) {
    return (
      <LockedSection 
        sectionId="doorCode"
        title={t('sections.doorCode.title', 'Codi d\'accés')}
        description={t('sections.doorCode.lockedDesc', 'El codi de la porta i habitació assignada')}
        icon={<IconKey className="w-6 h-6 text-gray-400" />}
      />
    )
  }
  
  // TODO: Implementar versió activa post-Beta
  return null
}

/**
 * Secció Com Arribar - Indicacions, mapa, parking
 */
export function SectionDirections({ locked = true }: { locked?: boolean }) {
  const { t } = useTranslation('guestPortal')
  
  if (locked) {
    return (
      <LockedSection 
        sectionId="directions"
        title={t('sections.directions.title', 'Com arribar')}
        description={t('sections.directions.lockedDesc', 'Indicacions, mapa i informació de parking')}
        icon={<IconMapPin className="w-6 h-6 text-gray-400" />}
      />
    )
  }
  
  // TODO: Implementar versió activa post-Beta
  return null
}

/**
 * Secció Personalitzada - Contingut específic per reserva
 */
export function SectionCustom({ locked = true }: { locked?: boolean }) {
  const { t } = useTranslation('guestPortal')
  
  if (locked) {
    return (
      <LockedSection 
        sectionId="custom"
        title={t('sections.custom.title', 'Informació personalitzada')}
        description={t('sections.custom.lockedDesc', 'Missatges específics per a la teva reserva')}
        icon={<IconMessage className="w-6 h-6 text-gray-400" />}
      />
    )
  }
  
  // TODO: Implementar versió activa post-Beta
  return null
}
